import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

type Props = {
  /** Sem título e sem ação, o cabeçalho inteiro não é renderizado. */
  titulo?: ReactNode
  /** Canto direito do cabeçalho: botão, segmented, seletor de período. */
  acao?: ReactNode
  children: ReactNode
  className?: string
  corpoClassName?: string
  /** Para conteúdo que sangra até a borda — tabela, gráfico, mapa de calor. */
  semPadding?: boolean
  /**
   * Cabeçalho de 36px em vez dos 46px padrão. Usado na tela de reunião projetada, que
   * empilha muitos painéis e precisa do espaço vertical para o conteúdo.
   */
  cabecalhoCompacto?: boolean
}

/**
 * Superfície padrão do sistema: raio 14, borda `--border`, cabeçalho de 46px
 * separado do corpo por `--border-subtle`.
 *
 * É o único painel do projeto. Antes existiam três raios diferentes para a
 * mesma ideia (`rounded-lg`, `rounded-xl`, `rounded-2xl`) espalhados por ~340
 * construções manuais — usar este componente é o que impede o quarto raio de
 * aparecer na próxima tela.
 *
 * Para caber num grid de altura uniforme, passe `className="h-full"`.
 */
export function Painel({ titulo, acao, children, className, corpoClassName, semPadding, cabecalhoCompacto }: Props) {
  const temCabecalho = titulo !== undefined || acao !== undefined

  return (
    <section
      className={cn('overflow-hidden rounded-painel border border-border bg-surface', className)}
    >
      {temCabecalho && (
        <header className={cn('flex items-center justify-between gap-3 border-b border-border-subtle px-[18px]', cabecalhoCompacto ? 'h-9' : 'h-[46px]')}>
          {typeof titulo === 'string' ? (
            <h2 className="text-titulo-painel min-w-0 truncate text-foreground">{titulo}</h2>
          ) : (
            titulo
          )}
          {acao !== undefined && <div className="flex shrink-0 items-center gap-2">{acao}</div>}
        </header>
      )}
      <div className={cn(!semPadding && 'p-[18px]', corpoClassName) || undefined}>{children}</div>
    </section>
  )
}
